import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody, TextCaption, H6 } from '@/design-system/components/typography';
import { ScrollView, SxProp, View } from 'dripsy';
import { isEmpty, isString } from 'lodash';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import {
  AccountsTransfersStackParamList,
  AccountsTransfersStackRoutes
} from '@/features/account-management/screens/accounts-transfers/type';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { errorParser } from '@/features/bill-payment/screens/otp/type';
import { formatAmountByDecimal } from '@/features/account-management/utils';

export interface AccountsTransfersProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  account: string;
  tab?: number;
  data: any;
  func?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
  onChangeView?: () => void;
  record?: any;
}

export const AccountsTransfersWebOneScreen: React.FC<AccountsTransfersProps> = (props) => {
  const { onClose, onLayout, account, tab, data, func, onReturnData, refresh, onChangeView, record } = props;
  const { t } = useTranslation('account-transfer');
  const layout = useContext(LayoutContext);
  const { body, textMb, textTitle, textAtmPin, btnView, btn } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<AccountsTransfersStackParamList>>();
  const [alertMessage, setAlertMessage] = useState('');
  const [isLoading, setIsloanding] = useState(false);

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        maxHeight: layout.dimensions.height,
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <ModalHeader
        title={t('account-transfer::instruction-confirmation.title--transfer-confirmation')}
        closeModal={() => navigation.getParent()?.goBack()}
      />
      <ScrollView sx={{ flex: 1, backgroundColor: 'transparent' }} nestedScrollEnabled>
        <View sx={body}>
          <TextBody semiBold>{t('account-transfer::instruction-confirmation.text--account-transfer-confirmation')}</TextBody>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('account-transfer::glossary.from-account')}
            </TextCaption>
            <H6 sx={{ ...textAtmPin, mb: '$1' }}>
              {data?.sourceAccount?.accountLabel
                ? t(`earn-invest::glossary.${data?.sourceAccount?.accountLabel}`)
                : data?.sourceAccount?.accountName || ' - '}
            </H6>
            <TextBody>{data.sourceAccount.accountNumber}</TextBody>
          </View>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('account-transfer::glossary.to-account')}
            </TextCaption>
            <H6 sx={{ ...textAtmPin, mb: '$1' }}>
              {data?.destinationAccount?.accountLabel
                ? t(`earn-invest::glossary.${data?.destinationAccount?.accountLabel}`)
                : data?.destinationAccount?.accountName || ' - '}
            </H6>
            <TextBody>{data.destinationAccount.accountNumber}</TextBody>
          </View>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('account-transfer::glossary.amount')}
            </TextCaption>
            <H6 sx={textAtmPin}>
              {formatAmountByDecimal({ value: data.amount, decimals: 2 })} {data?.asset}
            </H6>
          </View>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('account-transfer::glossary.description-optional')}
            </TextCaption>
            <H6 sx={textAtmPin}>{data.description || '-'}</H6>
          </View>
          <View>
            <TextCaption sx={textTitle} bold>
              {t('card-account-management::glossary.xmetadata')}
            </TextCaption>
            {Object.keys(data.xmetadata).map((k) => (
              <View sx={{ pl: '$5', my: '$3' }}>
                <TextCaption sx={textTitle} semiBold>
                  {k}
                </TextCaption>
                <H6 sx={textAtmPin}>{data.xmetadata[k]}</H6>
              </View>
            ))}
          </View>
          {!isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
          <View sx={btnView}>
            <UniButton
              sx={btn}
              variant={ButtonVariant.outline1}
              label={t('account-transfer::glossary.button-back')}
              onPress={() => {
                navigation?.goBack();
                onChangeView && onChangeView();
              }}
            />
            <UniButton
              sx={btn}
              labelSx={{ color: '$balck' }}
              label={t('account-transfer::instruction-confirmation.button--confirm')}
              loading={isLoading}
              disabled={isLoading}
              onPress={() => {
                setIsloanding(true);
                func &&
                  func(
                    {
                      amount: data.amount,
                      asset: data.asset,
                      sourceAccountId: data.sourceAccount.id,
                      destinationAccountId: data.destinationAccount.id,
                      description: data.description,
                      xmetadata: Object.keys(data.xmetadata).length ? data.xmetadata : undefined
                    },
                    (returnData) => {
                      setIsloanding(false);
                      onReturnData && onReturnData(returnData.data);
                      navigation.navigate(AccountsTransfersStackRoutes.Placed, {
                        record,
                        onLayout: onLayout,
                        account,
                        tab,
                        data,
                        returnData: returnData.data,
                        refresh
                      });
                    },
                    (error) => {
                      setIsloanding(false);
                      const data: any = error.response?.data;
                      const errorMessage = data && isString(data.message) ? errorParser(data.message, t) : errorParser(error.message, t);
                      setAlertMessage(errorMessage);
                    }
                  );
              }}
            />
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28,
    gap: '$5'
  },
  textMb: {
    marginBottom: 20
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '$3',
    marginTop: 20
  },
  btn: {
    width: 216,
    height: 44
  }
};
